import { EnabledProctoringOptions } from "@verity/api-client/types";
import { ProctoringAssessmentConfigurationV2, ProctoringMethod } from "@verity/proctoring-option/types";
import {
	CoreChip,
	CoreModal,
	CoreModalDescription,
	CoreRadioCard,
	CoreRadioCardIcon,
	CoreRadioCardSelectIcon,
	CoreSkeletonWrapper,
} from "../../core";
import { forwardRef, useRef, useState } from "react";
import i18nPackage from "../../i18n/i18n";
import { Typography } from "@mui/material";
import styles from "./MethodStep.module.css";
import StepDescription, { StepWrapper } from "./CommonStepComponents";
import { VerityInstituteSettings } from "../../types/common";
import { EditQuizPageProps } from "../editQuiz";
import { UseEditQuizStateReturn } from "../hooks/state/useEditQuizState";
import { METHOD_ICON_CONFIG } from "./StepIconConfig";
import { getAvailableProctoringMethods, isAutomatedProctoring } from "../editQuizHelper";
import type { UseEditQuizNavigationReturn } from "../hooks/useEditQuizNavigation";
import { QuickSetupHighlight } from "../../setup-guide";

function DeleteLiveSessionsModal({ onClose }: { onClose: () => void }) {
	return (
		<CoreModal
			open
			onClose={onClose}
			title={i18nPackage.t("editQuizPage.methodStep.deleteModalTitle")}
			showBottomCloseButton
			maxWidth="500px"
		>
			<CoreModalDescription>{i18nPackage.t("editQuizPage.methodStep.deleteUpcomingSessions")}</CoreModalDescription>
		</CoreModal>
	);
}

type MethodButtonProps = {
	type: ProctoringMethod;
	selected: boolean;
	onClick: () => void;
	loading?: boolean;
};

const MethodButton = forwardRef<HTMLDivElement, MethodButtonProps>(function MethodButton(
	{ selected, type, onClick, loading },
	ref,
) {
	const translationKey = type === "pop-in" ? "liveCheckIn" : type === "pop-in-post-review" ? "postReview" : type;
	const MethodIcon = METHOD_ICON_CONFIG[type];

	return (
		<CoreSkeletonWrapper loading={loading} height={"100%"}>
			<CoreRadioCard
				ref={ref}
				slotProps={{ button: { className: styles.methodButton } }}
				onClick={onClick}
				selected={selected}
			>
				<div className={styles.badgeRow}>
					<CoreChip chipColor="#F3E5F5" className={styles.methodBadge}>
						{i18nPackage.t(`editQuizPage.methodStep.${translationKey}.badge`)}
					</CoreChip>
					<CoreRadioCardSelectIcon selected={selected} />
				</div>
				<div className={styles.methodColumn}>
					<CoreRadioCardIcon icon={<MethodIcon />} enabled={selected} width={60} height={60} />
					<Typography variant="h2" className={styles.methodName}>
						{i18nPackage.t(`editQuizPage.methodStep.${translationKey}.header`)}
					</Typography>
					<Typography className={styles.methodDescription}>
						{i18nPackage.t(`editQuizPage.methodStep.${translationKey}.description`)}
					</Typography>
				</div>
			</CoreRadioCard>
		</CoreSkeletonWrapper>
	);
});

type MethodStepProps = {
	enabledProctoringOptions: EnabledProctoringOptions;
	updateOptions: (newOptions: Omit<Partial<ProctoringAssessmentConfigurationV2>, "proctoringOptionProfile">) => void;
	hasUpcomingSessions: () => boolean;
	verityInstituteSettings: VerityInstituteSettings;
	options: NonNullable<EditQuizPageProps["proctoringAssessmentConfiguration"]>;
	loading?: boolean;
} & Pick<UseEditQuizStateReturn["nav"]["action"], "unmarkStepAsCompleted"> &
	Pick<UseEditQuizNavigationReturn, "setupGuideState">;

export default function MethodStep({
	enabledProctoringOptions,
	updateOptions,
	hasUpcomingSessions,
	unmarkStepAsCompleted,
	options,
	verityInstituteSettings,
	loading,
	setupGuideState,
}: MethodStepProps) {
	const [deleteSessionsModalShowing, setDeleteSessionsModalShowing] = useState(false);
	const automatedCardRef = useRef<HTMLDivElement>(null);
	const livePopinCardRef = useRef<HTMLDivElement>(null);
	const gridRef = useRef<HTMLDivElement>(null);

	const setLiveMode = (mode: ProctoringMethod) => {
		if (mode !== "live" && options.isLiveProctored && hasUpcomingSessions()) {
			setDeleteSessionsModalShowing(true);
		} else {
			updateOptions({
				...options,
				isLiveProctored: mode === "live",
				isLivePopIn: mode === "pop-in" || mode === "pop-in-post-review",
				enablePostReview: mode === "pop-in-post-review",
			});
		}
	};

	const availableMethods = getAvailableProctoringMethods(enabledProctoringOptions, verityInstituteSettings);

	const showHighlight = setupGuideState && setupGuideState.highlightTarget !== "next";
	const activeRef =
		setupGuideState?.highlightTarget === "default"
			? automatedCardRef
			: setupGuideState?.highlightTarget === "live-check-in"
				? livePopinCardRef
				: gridRef;

	return (
		<StepWrapper>
			<div className={styles.methodContainer}>
				<StepDescription loading={loading}>{i18nPackage.t("editQuizPage.methodStep.description")}</StepDescription>

				<div className={styles.methodGrid} ref={gridRef}>
					{availableMethods.includes("automated") && (
						<MethodButton
							type="automated"
							ref={automatedCardRef}
							onClick={() => {
								if (options.isLiveProctored || options.isLivePopIn) {
									unmarkStepAsCompleted("liveSessions");
									setLiveMode("automated");
								}
							}}
							selected={isAutomatedProctoring(options)}
							loading={loading}
						/>
					)}
					{availableMethods.includes("live") && (
						<MethodButton
							type="live"
							onClick={() => {
								if (!options.isLiveProctored) {
									setLiveMode("live");
								}
							}}
							selected={options.isLiveProctored ?? false}
							loading={loading}
						/>
					)}
					{availableMethods.includes("pop-in") && (
						<MethodButton
							ref={livePopinCardRef}
							type="pop-in"
							onClick={() => {
								if (!options.isLivePopIn || options.enablePostReview) {
									if (!options.isLivePopIn) {
										unmarkStepAsCompleted("liveSessions");
									}
									setLiveMode("pop-in");
								}
							}}
							selected={(options.isLivePopIn && !options.enablePostReview) ?? false}
							loading={loading}
						/>
					)}
					{availableMethods.includes("pop-in-post-review") && (
						<MethodButton
							type="pop-in-post-review"
							onClick={() => {
								if (!options.isLivePopIn || !options.enablePostReview) {
									if (!options.isLivePopIn) {
										unmarkStepAsCompleted("liveSessions");
									}
									setLiveMode("pop-in-post-review");
								}
							}}
							selected={(options.isLivePopIn && options.enablePostReview) ?? false}
							loading={loading}
						/>
					)}
				</div>
				{deleteSessionsModalShowing && <DeleteLiveSessionsModal onClose={() => setDeleteSessionsModalShowing(false)} />}
				{setupGuideState && showHighlight && (
					<QuickSetupHighlight targetRef={activeRef} position="right" description={setupGuideState.description} />
				)}
			</div>
		</StepWrapper>
	);
}
